import { centavosDeDigitos, centavosDeNota, formatarCentavosCampo } from '@entregas/core';
import { type InputHTMLAttributes, useId, useState } from 'react';
import css from './Campo.module.css';

interface Props
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'inputMode'> {
  rotulo: string;
  valor: number | null;
  aoMudar: (centavos: number | null) => void;
  erro?: string | undefined;
  dica?: string | undefined;
  /**
   * `centavos` (padrão): os dígitos entram pela direita, como na maquininha (1-1-5-0-0 vira 115,00).
   * `nota`: a nota do cliente, em reais ("150" vira R$ 150,00; "50,50" também vale).
   */
  modo?: 'centavos' | 'nota';
}

/** 15000 → "150"; 15050 → "150,50": como a pessoa escreveria a nota. */
function textoDaNota(centavos: number | null): string {
  return formatarCentavosCampo(centavos).replace(/,00$/, '');
}

/**
 * Dinheiro sem vírgula nem ponto para digitar e sem erro de digitação. No modo `nota`, enquanto a pessoa
 * digita o campo mostra o que ela escreveu; ao sair, o valor formatado.
 */
export function CampoMoeda({
  rotulo,
  valor,
  aoMudar,
  erro,
  dica,
  modo = 'centavos',
  className,
  id: idExterno,
  onFocus,
  onBlur,
  ...resto
}: Props) {
  const idGerado = useId();
  const id = idExterno ?? idGerado;
  const descricao = erro ? `${id}-erro` : dica ? `${id}-dica` : undefined;
  const nota = modo === 'nota';
  const [digitando, setDigitando] = useState<string | null>(null);
  return (
    <div className={css.campo}>
      <label className={css.rotulo} htmlFor={id}>
        {rotulo}
      </label>
      <div className={css.comPrefixo}>
        <span className={css.prefixo} aria-hidden="true">
          R$
        </span>
        <input
          id={id}
          type="text"
          inputMode={nota ? 'decimal' : 'numeric'}
          autoComplete="off"
          placeholder={nota ? '0' : '0,00'}
          className={[css.controle, css.numero, erro && css.comErro, className]
            .filter(Boolean)
            .join(' ')}
          value={nota && digitando !== null ? digitando : formatarCentavosCampo(valor)}
          onChange={(e) => {
            if (!nota) return aoMudar(centavosDeDigitos(e.target.value));
            setDigitando(e.target.value);
            aoMudar(centavosDeNota(e.target.value));
          }}
          onFocus={(e) => {
            if (nota) setDigitando(textoDaNota(valor));
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setDigitando(null);
            onBlur?.(e);
          }}
          aria-invalid={erro ? true : undefined}
          aria-describedby={descricao}
          {...resto}
        />
      </div>
      {dica && !erro && (
        <span id={`${id}-dica`} className={css.dica}>
          {dica}
        </span>
      )}
      {erro && (
        <span id={`${id}-erro`} className={css.erro} role="alert">
          {erro}
        </span>
      )}
    </div>
  );
}
